import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "./ProductDetail.scss";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import {
  ImageSelector,
  ProductDescription,
  ProductVariationSelector,
  QuantitySelector,
  Button,
  ButtonType,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
} from "@amzn/burberry-ui-components";
import { awsRum } from "src/rum";
import { AppDispatch } from "src/store/store";
import { getStage } from "src/utils/roadieHelper";
import {
  hydrateCatalog,
  selectCatalog,
  selectHydrateCatalogComplete,
  selectCatalogError,
} from "src/store/catalogSlice";
import { addToCart } from "src/store/cartSlice";
import { selectCsrfToken } from "src/store/authSlice";
import { metricEmitter } from "src/utils/metricEmitter";
import { getPriceNumberFromString, findCatalogItemUsingPurchaseItem, UH_OH, GENERIC_ERROR_STRING, ADD_TO_CART_SUCCESS, AlertType } from "@amzn/d2c-application-website-common";

const ProductDetail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { asin } = useParams<{ asin: string }>();
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const csrfToken = useSelector(selectCsrfToken);

  const [quantity, setQuantity] = useState(1);
  const [selectedVariationIndexes, setSelectedVariationIndexes] = useState<
    number[]
  >([]);
  const [selectedImage, setSelectedImage] = useState("");
  const [pageLoadTime, setPageLoadTime] = useState(Date.now());
  const hasInitializedVariations = useRef(false);

  const catalogItem = findCatalogItemUsingPurchaseItem(catalog || [], { asin });

  // Reset state when navigating to a different product
  useEffect(() => {
    setSelectedImage("");
    setQuantity(1);
    setPageLoadTime(Date.now());
    hasInitializedVariations.current = false;
  }, [asin]);

  // Initialize variation indexes when catalog loads
  useEffect(() => {
    if (!hasInitializedVariations.current && catalogItem?.variations?.dimensions) {
      setSelectedVariationIndexes(
        catalogItem.variations.dimensions.map(() => 0),
      );
      hasInitializedVariations.current = true;
    }
  }, [catalogItem]);

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent("PageView", {
      page: "ProductDetail",
      asin: asin || "",
    });
  }, [asin]);

  // Page load metric
  useEffect(() => {
    if (hydrateCatalogComplete && catalogItem) {
      const loadTime = Date.now() - pageLoadTime;
      awsRum?.recordEvent("PageLoad", {
        page: "ProductDetail",
        loadTimeMs: String(loadTime),
      });
    }
  }, [hydrateCatalogComplete, catalogItem]);

  // Fetch catalog data
  useEffect(() => {
    if (asin) {
      dispatch(
        hydrateCatalog({
          itemIdentifiers: [{ itemAsin: asin }],
          stage: getStage(),
          locale: "en-US",
          csrfToken,
        }),
      );
    }
  }, [dispatch, asin, csrfToken]);

  // Set initial selected image
  useEffect(() => {
    const urls = getImageUrls();
    if (urls.length > 0 && !selectedImage) {
      setSelectedImage(urls[0]);
    }
  }, [catalogItem]);

  const handleAddToCart = () => {
    if (!catalogItem) return;
    awsRum?.recordEvent("ButtonClick", {
      action: "AddToCart",
      asin: asin || "",
      quantity: String(quantity),
    });
    dispatch(
      addToCart({
        purchaseItem: {
          quantity,
          asin: catalogItem.identifier?.itemAsin || "",
          offerId: catalogItem.offerId || "",
          merchantId: catalogItem.merchantId || "",
        },
        quantity,
        subtotal:
          quantity *
          getPriceNumberFromString(catalogItem.price || "0"),
      }),
    );
    setAlertType(AlertType.SUCCESS);
    setAlertMessage(ADD_TO_CART_SUCCESS);
    setAlertTrigger(prev => prev + 1);
    setShowAlert(true);
  };

  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>(AlertType.SUCCESS);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertTrigger, setAlertTrigger] = useState(0);

  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => setShowAlert(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [alertTrigger, showAlert]);

  const handleVariationChange = (
    dimensionIndex: number,
    valueIndex: number,
  ) => {
    awsRum?.recordEvent("VariationSelect", {
      asin: asin || "",
      dimensionIndex: String(dimensionIndex),
      valueIndex: String(valueIndex),
    });
    const newIndexes = [...selectedVariationIndexes];
    newIndexes[dimensionIndex] = valueIndex;
    setSelectedVariationIndexes(newIndexes);
  };

  const handleQuantityChange = (newQuantity: number) => {
    awsRum?.recordEvent("QuantityChange", {
      asin: asin || "",
      quantity: String(newQuantity),
    });
    setQuantity(newQuantity);
  };

  const handleImageSelect = (image: string) => {
    awsRum?.recordEvent("ImageSelect", { asin: asin || "", image });
    setSelectedImage(image);
  };

  // Get flat image URLs from catalog item
  const getImageUrls = (): string[] => {
    if (!catalogItem?.images) return [];
    return catalogItem.images.map((img: any) =>
      typeof img === "string" ? img : img.highRes?.url || img.lowRes?.url || "",
    ).filter(Boolean);
  };

  // Loading state
  if (!hydrateCatalogComplete && !catalogError) {
    return (
      <div
        className="productDetailPage productDetailPage--loading"
        data-testid="productDetailPage"
      >
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  // Error state
  if (catalogError || !catalogItem) {
    return (
      <div
        className="productDetailPage productDetailPage--error"
        data-testid="productDetailPage"
      >
        <ErrorBox
          heading={UH_OH}
          message={GENERIC_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  const imageUrls = getImageUrls();

  return (
    <div className="productDetailPage" data-testid="productDetailPage">
      <div className="productDetailPage__images">
        <ImageSelector
          images={imageUrls}
          selectedImage={selectedImage || imageUrls[0] || ""}
          productName={catalogItem.name || ""}
          onImageSelect={handleImageSelect}
        />
      </div>

      <div className="productDetailPage__info">
        <ProductDescription
          title={catalogItem.name || ""}
          price={catalogItem.price || ""}
          details={catalogItem.descriptionBullets}
          availability={catalogItem.availability?.type}
        />

        {catalogItem.variations?.dimensions &&
          catalogItem.variations.dimensions.length > 0 && (
            <ProductVariationSelector
              dimensions={catalogItem.variations.dimensions.map(
                (dim: any, i: number) => ({
                  name: dim.name || "",
                  values: dim.values || [],
                }),
              )}
              selectedValues={selectedVariationIndexes}
              onVariationChange={handleVariationChange}
            />
          )}

        <QuantitySelector
          quantity={quantity}
          maxQuantity={catalogItem.availability?.maxOrderQuantity || 99}
          setQuantity={handleQuantityChange}
          metricEmitter={metricEmitter}
        />

        <Button
          type={ButtonType.ADD_TO_CART}
          onClick={handleAddToCart}
          metricEmitter={metricEmitter}
          disabled={
            !catalogItem.availability ||
            catalogItem.availability.type === "UNAVAILABLE"
          }
        />
      </div>
      {showAlert && (
        <div className="pdp-alert-container" data-testid="alertContainer">
          <Alert
            icon={false}
            variant="filled"
            severity={alertType}
            action={alertType === AlertType.SUCCESS ? <Link to="/cart">View Cart</Link> : undefined}
          >
            {alertMessage}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
